import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, MessageCircle, AlertCircle } from 'lucide-react';
import { Layout } from './Layout';
import { useCompany } from '../contexts/CompanyContext';
import { drivers } from '../lib/api';
import { notifyActivationTeam, sendCongratulationsMessage } from '../utils/whatsapp';
import { cn } from '../utils/cn';
import type { ModuleResult } from '../types';
import { registerServiceWorker, sendPushNotification } from '../utils/notifications';
import toast from 'react-hot-toast';

interface DriverFormProps {
  score: number;
  moduleResults?: ModuleResult[];
}

function maskInput(value: string, pattern: string) {
  let result = '';
  let valueIndex = 0;

  for (let i = 0; i < pattern.length && valueIndex < value.length; i++) {
    if (pattern[i] === '#') {
      result += value[valueIndex++];
    } else {
      result += pattern[i];
    }
  }

  return result;
}

export function DriverForm({ score, moduleResults }: DriverFormProps) {
  const { company } = useCompany();
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    city: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<{id: string, name: string}[]>([]);
  const submitLockRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const storedCities = localStorage.getItem('cities');
    if (storedCities) {
      setCities(JSON.parse(storedCities));
    } else {
      const defaultCities = [
        { id: 'anapolisgo', name: 'Anápolis' },
        { id: 'rioverdego', name: 'Rio Verde' },
        { id: 'itumbiara', name: 'Itumbiara' }
      ];
      setCities(defaultCities);
      localStorage.setItem('cities', JSON.stringify(defaultCities));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitLockRef.current || loading || submitted) {
      console.log('Preventing duplicate submission:', { 
        submitLock: submitLockRef.current,
        loading, 
        submitted 
      });
      return;
    }

    submitLockRef.current = true;
    setError(null);
    setLoading(true);

    try {
      const cleanCPF = formData.cpf.replace(/\D/g, '');
      if (cleanCPF.length !== 11) {
        throw new Error('CPF inválido - Digite os 11 números do CPF');
      }

      if (!formData.email.includes('@')) {
        throw new Error('Email inválido - Digite um email válido com @');
      }

      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        throw new Error('Telefone inválido - Digite um número de telefone válido com DDD');
      }

      if (!formData.city) {
        throw new Error('Cidade não selecionada - Selecione sua cidade');
      }

      if (formRef.current) {
        const elements = formRef.current.elements;
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).setAttribute('disabled', 'true');
        }
      }

      console.log('Sending driver data:', {
        ...formData,
        cpf: cleanCPF,
        phone: cleanPhone,
        companyId: company.id
      });

      const response = await drivers.create(
        {
          name: formData.name,
          cpf: cleanCPF,
          email: formData.email,
          phone: cleanPhone,
          city: formData.city,
          companyId: company.id
        },
        moduleResults?.map(result => ({
          moduleId: result.moduleId,
          score: result.score,
          status: 'pending',
          moduleResults: result.results,
          completedAt: new Date().toISOString()
        })) || []
      );

      try {
        await sendPushNotification({
          title: 'Novo Condutor Registrado',
          body: `${formData.name} completou o treinamento com ${score.toFixed(1)}% de aproveitamento.`,
          data: {
            driverId: response.driver.id,
            score,
            city: formData.city
          }
        });
      } catch (notificationError) {
        console.warn('Failed to send push notification:', notificationError);
      }

      notifyActivationTeam({
        name: formData.name,
        cpf: cleanCPF,
        email: formData.email,
        city: formData.city,
        score
      });

      sendCongratulationsMessage(
        cleanPhone,
        formData.name,
        score
      );

      if (response.certificateUrl) {
        const link = document.createElement('a');
        link.href = response.certificateUrl;
        link.download = `certificado-${formData.name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setSubmitted(true);
      toast.success('Dados salvos com sucesso!', {
        duration: 4000,
        position: 'top-right'
      });

    } catch (err: any) {
      console.error('Error saving data:', err);
      let errorMessage = 'Não foi possível salvar os dados. ';
      
      if (err.code === '23505') {
        errorMessage = 'CPF já cadastrado. Por favor, verifique os dados.';
      } else if (err.message) {
        errorMessage += err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right'
      });
      
      if (formRef.current) {
        const elements = formRef.current.elements;
        for (let i = 0; i < elements.length; i++) {
          (elements[i] as HTMLElement).removeAttribute('disabled');
        }
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        submitLockRef.current = false;
      }, 5000);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let maskedValue = value;

    if (name === 'cpf') {
      const digits = value.replace(/\D/g, '');
      maskedValue = maskInput(digits, '###.###.###-##');
    } else if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      maskedValue = maskInput(digits, '(##) #####-####');
    }

    setFormData(prev => ({ ...prev, [name]: maskedValue }));
    setError(null);
  };

  const handleSupportClick = () => {
    const supportMessage = error 
      ? `Olá! Preciso de ajuda com o certificado do treinamento Zumm.\n\nErro: ${error}\n\nDados:\nNome: ${formData.name}\nCPF: ${formData.cpf}\nEmail: ${formData.email}\nCidade: ${formData.city}\nTelefone: ${formData.phone}`
      : 'Olá! Preciso de ajuda com o certificado do treinamento Zumm.';
    
    const encodedMessage = encodeURIComponent(supportMessage);
    window.open(`https://wa.me/5562986510948?text=${encodedMessage}`, '_blank');
  };

  return (
    <Layout hideNavigation>
      {submitted ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 text-center">
            <div className="flex flex-col items-center">
              <img
                src={company.logo}
                alt={company.name}
                className="h-20 w-auto mb-8"
              />
              <div className="rounded-full p-4 mb-6 bg-green-100">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Parabéns!
              </h2>
              <p className="mt-3 text-gray-600 text-lg">
                Você concluiu o treinamento com sucesso.
                Sua pontuação final foi {score.toFixed(1)}%.
              </p>
              <p className="mt-4 text-gray-500">
                Em breve nossa equipe entrará em contato via WhatsApp.
              </p>

              <button
                onClick={handleSupportClick}
                className={cn(
                  "mt-8 inline-flex items-center gap-2 rounded-lg px-6 py-3 text-white",
                  "transition-all hover:opacity-90 active:scale-95 bg-[#7c3aed]"
                )}
              >
                <MessageCircle className="h-5 w-5" />
                Precisa de ajuda? Fale conosco
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <img
                src={company.logo}
                alt={company.name}
                className="mx-auto h-20 w-auto mb-6"
              />
              <h2 className="text-3xl font-bold text-gray-900">
                Parabéns!
              </h2>
              <p className="mt-3 text-gray-600 text-lg">
                Você completou todos os módulos com {score.toFixed(1)}% de aproveitamento.
              </p>
              <p className="mt-2 text-gray-500">
                Preencha seus dados para receber o certificado.
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-lg bg-red-50 p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                    <button
                      onClick={handleSupportClick}
                      className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
                    >
                      Falar com o Suporte
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm p-8">
              <form ref={formRef} className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Nome Completo
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={cn(
                        "mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                        "shadow-sm transition-colors focus:border-purple-500 focus:ring-purple-500"
                      )}
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="cpf" className="block text-sm font-medium text-gray-700">
                      CPF
                    </label>
                    <input
                      id="cpf"
                      name="cpf"
                      type="text"
                      required
                      maxLength={14}
                      value={formData.cpf}
                      onChange={handleInputChange}
                      className={cn(
                        "mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                        "shadow-sm transition-colors focus:border-purple-500 focus:ring-purple-500"
                      )}
                      placeholder="000.000.000-00"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      WhatsApp
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="text"
                      required
                      maxLength={15}
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={cn(
                        "mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                        "shadow-sm transition-colors focus:border-purple-500 focus:ring-purple-500"
                      )}
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={cn(
                        "mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                        "shadow-sm transition-colors focus:border-purple-500 focus:ring-purple-500"
                      )}
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      Cidade
                    </label>
                    <select
                      id="city"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleInputChange}
                      className={cn(
                        "mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                        "shadow-sm transition-colors focus:border-purple-500 focus:ring-purple-500"
                      )}
                    >
                      <option value="">Selecione sua cidade</option>
                      {cities.map(city => (
                        <option key={city.id} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || submitted || submitLockRef.current}
                  className={cn(
                    "w-full flex justify-center items-center gap-2 rounded-lg",
                    "border border-transparent py-3 px-4 text-base font-medium text-white",
                    "shadow-sm transition-all hover:opacity-90 active:scale-[0.98] bg-[#7c3aed]",
                    "disabled:opacity-50 disabled:hover:opacity-50 disabled:active:scale-100"
                  )}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Salvando...
                    </>
                  ) : (
                    'Finalizar e Receber Certificado'
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSupportClick}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Precisa de ajuda? Fale com o suporte
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}